type DateOnlyLike = {
  year: number;
  month: number;
  day: number;
};

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;

export const NETLINE_CONFIGURATION_CODE = "99";

export const NETLINE_COLUMN_IDS = [
  "netlineCarrier",
  "netlineFlightNumber",
  "netlineOpSuffix",
  "netlineStartDate",
  "netlineEndDate",
  "netlineWeekdayPattern",
  "netlineOrigin",
  "netlineStd",
  "netlineSta",
  "netlineDestination",
  "netlineAircraftOwner",
  "netlineAircraftType",
  "netlineConfigurationCode",
  "netlineServiceType",
] as const;

export const NETLINE_HEADERS: Record<(typeof NETLINE_COLUMN_IDS)[number], string> = {
  netlineCarrier: "Carrier",
  netlineFlightNumber: "Flight Number",
  netlineOpSuffix: "OP Suffix",
  netlineStartDate: "Departure Date",
  netlineEndDate: "Departure Date",
  netlineWeekdayPattern: "Weekday Pattern",
  netlineOrigin: "Origin",
  netlineStd: "STD",
  netlineSta: "STA",
  netlineDestination: "Destination",
  netlineAircraftOwner: "Aircraft Owner",
  netlineAircraftType: "Aircraft Type",
  netlineConfigurationCode: "Configuration Code",
  netlineServiceType: "Service Type",
};

function isDateOnlyLike(value: unknown): value is DateOnlyLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "year" in value &&
    "month" in value &&
    "day" in value &&
    typeof value.year === "number" &&
    typeof value.month === "number" &&
    typeof value.day === "number"
  );
}

function getDateParts(value: unknown): DateOnlyLike | null {
  if (isDateOnlyLike(value)) {
    return value;
  }

  if (typeof value === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }

  return null;
}

function getValidatedUtcDate(value: unknown): Date | null {
  const parts = getDateParts(value);

  if (!parts) {
    return null;
  }

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

  if (
    date.getUTCFullYear() !== parts.year ||
    date.getUTCMonth() + 1 !== parts.month ||
    date.getUTCDate() !== parts.day
  ) {
    return null;
  }

  return date;
}

export function formatNetlineDate(value: unknown): string {
  const date = getValidatedUtcDate(value);

  if (!date) {
    return "";
  }

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = MONTHS[date.getUTCMonth()];
  const year = String(date.getUTCFullYear()).slice(-2);

  return `${day}${month}${year}`;
}

export function formatNetlineWeekdayPattern(value: unknown): string {
  const date = getValidatedUtcDate(value);

  if (!date) {
    return "";
  }

  const weekday = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  return Array.from({length: 7}, (_, index) => index + 1 === weekday ? String(weekday) : ".").join("");
}

export function formatNetlineTime(value: unknown): string {
  if (typeof value === "string") {
    const match = /(?:^|T|\s)(\d{2}):(\d{2})/.exec(value);

    if (match) {
      return `${match[1]}:${match[2]}`;
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const hours = String(value.getUTCHours()).padStart(2, "0");
    const minutes = String(value.getUTCMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  return "";
}

export function formatNetlineFileTimestamp(value: Date = new Date()): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  const hours = String(value.getHours()).padStart(2, "0");
  const minutes = String(value.getMinutes()).padStart(2, "0");

  return `${year}${month}${day}-${hours}${minutes}`;
}
